import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '../../contexts/AuthContext';
import {
  Globe, Image, Type, Save, Plus, Trash2, RefreshCw,
  Eye, EyeOff, GripVertical, Check, AlertCircle, Loader2,
  MapPin, Search, X, ChevronDown, LayoutGrid, FileText, Star, StarOff,
  Pencil, Sparkles, Building2, Calendar, MessageSquare, HelpCircle,
  LayoutDashboard, ExternalLink, Link, Package, Users
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'cities', label: 'Şehirler (Ana Sayfa)', icon: MapPin },
  { id: 'gallery', label: 'Proje Galerisi', icon: LayoutGrid },
  { id: 'texts', label: 'Metin İçerikleri', icon: FileText },
  { id: 'supplier_cards', label: 'Tedarikçi Kartları', icon: Package },
  { id: 'premium_suppliers', label: 'Premium Tedarikçiler', icon: Star },
  { id: 'testimonials', label: 'Referanslar & İstatistikler', icon: Users },
];

const MAX_FEATURED = 6;

// ── City Editor Modal ─────────────────────────────────────────────────────────
const CityEditorModal = ({ city, onClose, onSaved, token }) => {
  const [form, setForm] = useState({
    tagline: city.tagline || '',
    description: city.description || '',
    image: city.image || '',
    venues: (city.ai_content?.venues || []).map(v => `${v.name} | ${v.description || ''}`).join('\n'),
    major_events: (city.ai_content?.major_events || []).map(e => `${e.name} | ${e.date} | ${e.participants}`).join('\n'),
    faq: (city.ai_content?.faq || []).map(f => `${f.question}\n${f.answer}`).join('\n---\n'),
  });
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const parseVenues = (text) => text.split('\n').filter(Boolean).map(line => {
    const [name, desc] = line.split('|').map(s => s.trim());
    return { name: name || line, description: desc || '', area: '—', location: city.name };
  });
  const parseEvents = (text) => text.split('\n').filter(Boolean).map(line => {
    const [name, date, participants] = line.split('|').map(s => s.trim());
    return { name: name || line, date: date || '2025', participants: participants || '—' };
  });
  const parseFaq = (text) => text.split('\n---\n').filter(Boolean).map(block => {
    const lines = block.trim().split('\n');
    return { question: lines[0] || '', answer: lines.slice(1).join('\n') || '' };
  });

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const res = await axios.post(`${API}/ultra/city-pages/${city.slug}/regenerate-ai`, {}, { headers });
      const ai = res.data.ai_content || {};
      setForm({
        tagline: ai.tagline || form.tagline,
        description: ai.description || form.description,
        image: form.image,
        venues: (ai.venues || []).map(v => `${v.name} | ${v.description || ''}`).join('\n'),
        major_events: (ai.major_events || []).map(e => `${e.name} | ${e.date} | ${e.participants}`).join('\n'),
        faq: (ai.faq || []).map(f => `${f.question}\n${f.answer}`).join('\n---\n'),
      });
      toast.success('AI içerik yenilendi!');
    } catch { toast.error('AI üretim başarısız'); }
    finally { setRegenerating(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/ultra/city-pages/${city.slug}`, {
        tagline: form.tagline,
        description: form.description,
        image: form.image,
        ai_content: {
          ...(city.ai_content || {}),
          venues: parseVenues(form.venues),
          major_events: parseEvents(form.major_events),
          faq: parseFaq(form.faq),
        },
      }, { headers });
      toast.success(`${city.name} sayfası güncellendi!`);
      onSaved();
      onClose();
    } catch { toast.error('Kaydetme başarısız'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 backdrop-blur-sm p-4 pt-12">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <span className="text-xl">{city.flag || '🌍'}</span>
              {city.name} — Sayfa İçeriği Düzenle
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">Değişiklikler anında şehir sayfasına yansır</p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={regenerate} disabled={regenerating}
              className="gap-1.5 text-purple-600 border-purple-200 hover:bg-purple-50 text-xs">
              {regenerating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              AI ile Yenile
            </Button>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="h-5 w-5" /></button>
          </div>
        </div>

        {/* Form */}
        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 flex items-center gap-1 mb-1">
                <Type className="h-3 w-3" /> Slogan
              </label>
              <Input value={form.tagline} onChange={e => setForm(p => ({ ...p, tagline: e.target.value }))}
                placeholder="Şehir sloganı..." className="h-9 text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 flex items-center gap-1 mb-1">
                <Image className="h-3 w-3" /> Görsel URL
              </label>
              <Input value={form.image} onChange={e => setForm(p => ({ ...p, image: e.target.value }))}
                placeholder="https://..." className="h-9 text-sm" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 flex items-center gap-1 mb-1">
              <FileText className="h-3 w-3" /> Açıklama
            </label>
            <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Şehrin fuar önemine dair açıklama..." />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 flex items-center gap-1 mb-1">
              <Building2 className="h-3 w-3" /> Fuar Mekanları
              <span className="text-gray-400 font-normal">(her satır: Ad | Açıklama)</span>
            </label>
            <textarea value={form.venues} onChange={e => setForm(p => ({ ...p, venues: e.target.value }))}
              rows={4} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="COEX Convention Center | Güney Kore'nin en büyük fuar merkezi&#10;Kintex | Uluslararası fuarlara ev sahipliği" />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 flex items-center gap-1 mb-1">
              <Calendar className="h-3 w-3" /> Büyük Fuarlar
              <span className="text-gray-400 font-normal">(Ad | Tarih | Ziyaretçi)</span>
            </label>
            <textarea value={form.major_events} onChange={e => setForm(p => ({ ...p, major_events: e.target.value }))}
              rows={4} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Seoul Food & Hotel | Mart 2025 | 80.000&#10;Korea Electronics Show | Kasım 2025 | 120.000" />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 flex items-center gap-1 mb-1">
              <HelpCircle className="h-3 w-3" /> SSS (Sık Sorulan Sorular)
              <span className="text-gray-400 font-normal">(Soru / Cevap / --- ile ayır)</span>
            </label>
            <textarea value={form.faq} onChange={e => setForm(p => ({ ...p, faq: e.target.value }))}
              rows={6} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Soru buraya&#10;Cevap buraya&#10;---&#10;İkinci soru&#10;İkinci cevap" />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-5 border-t bg-gray-50 rounded-b-2xl">
          <Button variant="outline" onClick={onClose} className="text-sm">İptal</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white gap-2 text-sm">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Kaydet
          </Button>
        </div>
      </div>
    </div>
  );
};

// City card for FEATURED cities (shown on homepage)
const FeaturedCityCard = ({ city, index, onUpdate, onDelete, onFetchImage, fetching }) => {
  const [localName, setLocalName] = useState(city.name);
  const [localSuppliers, setLocalSuppliers] = useState(city.suppliers);

  return (
    <div data-testid={`featured-city-card-${index}`} className="bg-white border-2 border-blue-200 rounded-xl overflow-hidden group hover:border-blue-400 transition-all relative">
      <div className="absolute top-2 left-2 z-10">
        <span className="bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
          <Star className="h-2.5 w-2.5 fill-current" /> ÖNE ÇIKAN
        </span>
      </div>
      <div className="relative h-32 overflow-hidden bg-gray-100">
        {city.image ? (
          <img src={city.image} alt={city.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs">No image</div>
        )}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <Button size="sm" variant="secondary"
            onClick={() => onFetchImage(index, localName)}
            disabled={fetching === index}
            className="h-7 text-xs gap-1 bg-white/90 text-gray-900 hover:bg-white"
          >
            {fetching === index ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Görsel
          </Button>
        </div>
      </div>
      <div className="p-3 space-y-2">
        <Input value={localName} onChange={e => setLocalName(e.target.value.toUpperCase())}
          onBlur={() => onUpdate(index, { name: localName, slug: localName.toLowerCase().replace(/\s+/g, '-'), suppliers: localSuppliers, image: city.image })}
          className="h-7 text-xs font-semibold" placeholder="ŞEHİR ADI" />
        <Input value={localSuppliers} onChange={e => setLocalSuppliers(e.target.value)}
          onBlur={() => onUpdate(index, { name: localName, slug: city.slug, suppliers: localSuppliers, image: city.image })}
          className="h-7 text-xs" placeholder="15 suppliers" />
        <Input value={city.image || ''} onChange={e => onUpdate(index, { ...city, image: e.target.value })}
          className="h-7 text-xs" placeholder="Görsel URL (opsiyonel)" />
        <button onClick={() => onDelete(index)}
          className="w-full h-7 flex items-center justify-center gap-1 text-xs text-red-500 hover:bg-red-50 rounded-lg border border-transparent hover:border-red-200 transition-colors">
          <StarOff className="h-3 w-3" /> Öne Çıkarmayı Kaldır
        </button>
      </div>
    </div>
  );
};

const GalleryImageCard = ({ img, index, onUpdate, onDelete }) => (
  <div className="relative group bg-gray-100 rounded-xl overflow-hidden aspect-square">
    {img.url ? (
      <img src={img.url} alt={img.alt} className="w-full h-full object-cover" />
    ) : (
      <div className="w-full h-full flex items-center justify-center text-gray-300 text-sm">Empty</div>
    )}
    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-3">
      <input
        type="text" value={img.url || ''} placeholder="Image URL..."
        onChange={e => onUpdate(index, { ...img, url: e.target.value })}
        className="w-full text-xs bg-white/90 border-0 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-orange-400"
      />
      <input
        type="text" value={img.alt || ''} placeholder="Alt text..."
        onChange={e => onUpdate(index, { ...img, alt: e.target.value })}
        className="w-full text-xs bg-white/90 border-0 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-orange-400"
      />
      <button onClick={() => onDelete(index)}
        className="flex items-center gap-1 text-xs text-red-200 hover:text-red-100 transition-colors">
        <Trash2 className="h-3.5 w-3.5" />Remove
      </button>
    </div>
    <div className="absolute top-2 left-2 bg-black/50 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
      {index + 1}
    </div>
  </div>
);

const UltraCMS = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('cities');
  const [content, setContent] = useState(null);
  const [allCities, setAllCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fetchingImg, setFetchingImg] = useState(null);
  const [citySearch, setCitySearch] = useState('');
  const [saved, setSaved] = useState(false);
  const [togglingSlug, setTogglingSlug] = useState(null);
  const [editingCity, setEditingCity] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const loadData = async () => {
    if (!token) return;
    try {
      const [contentRes, allCitiesRes] = await Promise.all([
        axios.get(`${API}/ultra/homepage-content`, { headers }),
        axios.get(`${API}/ultra/city-pages`, { headers }),
      ]);
      setContent(contentRes.data);
      setAllCities(allCitiesRes.data.cities || []);
    } catch { toast.error('İçerik yüklenemedi'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      // Enforce max 6 featured cities before save
      const featured = (content.cities || []).slice(0, MAX_FEATURED);
      await axios.put(`${API}/ultra/homepage-content`, { ...content, cities: featured }, { headers });
      setSaved(true);
      toast.success('Ana sayfa içeriği kaydedildi!');
      setTimeout(() => setSaved(false), 2000);
    } catch { toast.error('Kaydetme başarısız'); }
    finally { setSaving(false); }
  };

  const featuredSlugs = (content?.cities || []).map(c => c.slug);
  const featuredCount = featuredSlugs.length;

  // ── Featured cities handlers ──────────────────────────────────────────────────
  const updateCity = (index, patch) => {
    setContent(prev => {
      const cities = [...prev.cities];
      cities[index] = { ...cities[index], ...patch };
      return { ...prev, cities };
    });
  };

  const removeFromFeatured = (index) => {
    setContent(prev => ({ ...prev, cities: prev.cities.filter((_, i) => i !== index) }));
  };

  const addToFeatured = (libCity) => {
    if (featuredCount >= MAX_FEATURED) {
      toast.error(`Maksimum ${MAX_FEATURED} şehir öne çıkarılabilir! Önce bir şehri kaldırın.`);
      return;
    }
    const slug = libCity.slug || libCity.name.toLowerCase().replace(/\s+/g, '-');
    if (featuredSlugs.includes(slug)) {
      toast.info('Bu şehir zaten öne çıkarılmış.');
      return;
    }
    setContent(prev => ({
      ...prev,
      cities: [...prev.cities, {
        name: (libCity.name || '').toUpperCase(),
        slug,
        suppliers: `${libCity.contractors_count || 0} contractors`,
        image: libCity.image || libCity.image_url || '',
      }],
    }));
    toast.success(`${libCity.name} öne çıkan şehirlere eklendi!`);
  };

  const fetchCityImage = async (index, cityName) => {
    setFetchingImg(index);
    try {
      const res = await axios.get(`${API}/ultra/city-image?city=${encodeURIComponent(cityName)}`, { headers });
      updateCity(index, { image: res.data.image_url });
      toast.success(res.data.in_library ? `${cityName} için görsel bulundu!` : `${cityName} için varsayılan görsel kullanıldı`);
    } catch { toast.error('Görsel alınamadı'); }
    finally { setFetchingImg(null); }
  };

  // ── Gallery handlers ─────────────────────────────────────────────────────────
  const updateGalleryImg = (index, patch) => {
    setContent(prev => {
      const images = [...prev.gallery.images];
      images[index] = { ...images[index], ...patch };
      return { ...prev, gallery: { ...prev.gallery, images } };
    });
  };

  const deleteGalleryImg = (index) => {
    setContent(prev => ({
      ...prev,
      gallery: { ...prev.gallery, images: prev.gallery.images.filter((_, i) => i !== index) },
    }));
  };

  const addGalleryImg = () => {
    const newId = String(Date.now());
    setContent(prev => ({
      ...prev,
      gallery: { ...prev.gallery, images: [...prev.gallery.images, { id: newId, url: '', alt: 'New Image' }] },
    }));
  };

  // ── Text update helper ───────────────────────────────────────────────────────
  const updateText = (section, field, value) => {
    setContent(prev => ({ ...prev, [section]: { ...(prev[section] || {}), [field]: value } }));
  };

  const filteredAllCities = citySearch
    ? allCities.filter(c => c.name.toLowerCase().includes(citySearch.toLowerCase()) || (c.country || '').toLowerCase().includes(citySearch.toLowerCase()))
    : allCities;

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* City Editor Modal */}
      {editingCity && (
        <CityEditorModal
          city={editingCity}
          token={token}
          onClose={() => setEditingCity(null)}
          onSaved={loadData}
        />
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ana Sayfa İçerik Yönetimi</h1>
          <p className="text-sm text-gray-500 mt-0.5">Herkese açık ana sayfa içeriğini düzenleyin ve yönetin</p>
        </div>
        <Button onClick={save} disabled={saving || saved} data-testid="cms-save-btn"
          className={`gap-2 transition-colors ${saved ? 'bg-emerald-500 hover:bg-emerald-500' : 'bg-orange-500 hover:bg-orange-600'} text-white font-semibold h-10 px-6`}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : saved ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}
          {saved ? 'Kaydedildi!' : 'Değişiklikleri Kaydet'}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === tab.id ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              <Icon className="h-4 w-4" />{tab.label}
            </button>
          );
        })}
      </div>

      {/* ── CITIES TAB ─────────────────────────────────────────────────────── */}
      {activeTab === 'cities' && content && (
        <div className="space-y-6">
          {/* Featured Cities Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-gray-800 flex items-center gap-2">
                  <Star className="h-4 w-4 text-blue-500 fill-current" />
                  Öne Çıkan Şehirler — Ana Sayfa Görünümü
                  <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${featuredCount >= MAX_FEATURED ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                    {featuredCount}/{MAX_FEATURED}
                  </span>
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {featuredCount >= MAX_FEATURED
                    ? '⚠️ Maksimum 6 şehre ulaşıldı. Eklemek için önce bir tanesini kaldırın.'
                    : `Ana sayfada gösterilecek şehirler. ${MAX_FEATURED - featuredCount} şehir daha eklenebilir.`}
                </p>
              </div>
            </div>

            {/* Featured Cities Grid */}
            {featuredCount === 0 ? (
              <div className="border-2 border-dashed border-blue-200 rounded-xl p-8 text-center text-gray-400">
                <Star className="h-8 w-8 mx-auto mb-2 text-blue-200" />
                <p className="text-sm">Henüz öne çıkan şehir yok. Aşağıdaki kütüphaneden şehir seçin.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {(content.cities || []).map((city, i) => (
                  <FeaturedCityCard key={i} city={city} index={i}
                    onUpdate={updateCity} onDelete={removeFromFeatured}
                    onFetchImage={fetchCityImage} fetching={fetchingImg} />
                ))}
              </div>
            )}
          </div>

          {/* Divider */}
          <hr className="border-gray-200" />

          {/* All Cities Library */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-gray-800 flex items-center gap-2">
                  <Globe className="h-4 w-4 text-gray-500" />
                  Tüm Şehir Kütüphanesi
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{allCities.length} şehir</span>
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  Öne çıkarmak istediğiniz şehirlerde "Öne Çıkar" butonuna tıklayın.
                  Otomatik eklenen şehirler yeni yükleniciler kaydolunca buraya eklenir.
                </p>
              </div>
              <div className="relative w-52">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <Input value={citySearch} onChange={e => setCitySearch(e.target.value)}
                  placeholder="Şehir ara..." className="pl-8 h-8 text-sm" />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {filteredAllCities.map(city => {
                const isFeatured = featuredSlugs.includes(city.slug);
                return (
                  <div key={city.slug} className={`relative rounded-xl overflow-hidden border-2 transition-all ${isFeatured ? 'border-blue-400 opacity-70' : 'border-gray-200 hover:border-gray-300'}`}>
                    {city.auto_created && (
                      <div className="absolute top-2 right-2 z-10">
                        <span className="bg-emerald-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">OTO</span>
                      </div>
                    )}
                    <div className="h-24 overflow-hidden bg-gray-100">
                      {city.image ? (
                        <img src={city.image} alt={city.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-2xl">{city.flag || '🌍'}</div>
                      )}
                    </div>
                    <div className="p-2.5">
                      <p className="text-xs font-bold text-gray-800 truncate">{city.name}</p>
                      <p className="text-[10px] text-gray-400">{city.country}</p>
                      {city.contractors_count > 0 && (
                        <p className="text-[10px] text-blue-600">{city.contractors_count} yüklenici</p>
                      )}
                      <button
                        onClick={() => isFeatured ? null : addToFeatured(city)}
                        disabled={isFeatured || featuredCount >= MAX_FEATURED}
                        data-testid={`feature-city-${city.slug}`}
                        className={`w-full mt-2 h-7 flex items-center justify-center gap-1 text-[11px] font-semibold rounded-lg transition-colors
                          ${isFeatured
                            ? 'bg-blue-50 text-blue-600 cursor-default border border-blue-200'
                            : featuredCount >= MAX_FEATURED
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                              : 'bg-blue-600 text-white hover:bg-blue-700 border border-blue-600'}`}
                      >
                        {isFeatured ? (
                          <><Star className="h-3 w-3 fill-current" /> Öne Çıkarıldı</>
                        ) : (
                          <><Star className="h-3 w-3" /> Öne Çıkar</>
                        )}
                      </button>
                      {/* Edit button */}
                      <button
                        onClick={() => setEditingCity(city)}
                        data-testid={`edit-city-${city.slug}`}
                        className="w-full mt-1 h-7 flex items-center justify-center gap-1 text-[11px] font-semibold rounded-lg bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200 transition-colors"
                      >
                        <Pencil className="h-3 w-3" /> Sayfayı Düzenle
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {filteredAllCities.length === 0 && (
              <div className="text-center py-8 text-gray-400">
                <Globe className="h-8 w-8 mx-auto mb-2" />
                <p className="text-sm">Şehir bulunamadı</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── GALLERY TAB ────────────────────────────────────────────────────── */}
      {activeTab === 'gallery' && content && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-700">Proje Galerisi Görselleri</p>
              <p className="text-xs text-gray-400 mt-0.5">Görselin üzerine gelin, URL'yi düzenleyin veya kaldırın.</p>
            </div>
            <Button size="sm" variant="outline" onClick={addGalleryImg} className="h-8 text-xs gap-1.5 border-gray-300">
              <Plus className="h-3.5 w-3.5" />Görsel Ekle
            </Button>
          </div>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">Bölüm Başlığı</label>
                <textarea value={content.gallery?.title || ''} onChange={e => updateText('gallery', 'title', e.target.value)}
                  rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-400" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">Alt Başlık</label>
                <textarea value={content.gallery?.subtitle || ''} onChange={e => updateText('gallery', 'subtitle', e.target.value)}
                  rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-400" />
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {(content.gallery?.images || []).map((img, i) => (
              <GalleryImageCard key={img.id || i} img={img} index={i} onUpdate={updateGalleryImg} onDelete={deleteGalleryImg} />
            ))}
          </div>
        </div>
      )}

      {/* ── TEXTS TAB ──────────────────────────────────────────────────────── */}
      {activeTab === 'texts' && content && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3 border-b border-gray-50">
              <CardTitle className="text-sm">"Made Easy & Quick" Bölümü</CardTitle>
            </CardHeader>
            <CardContent className="p-5 space-y-3">
              {[
                { key: 'label', label: 'Üst Etiket', placeholder: 'Trade Show Stand' },
                { key: 'heading', label: 'Ana Başlık (satır kesmek için \\n kullanın)', placeholder: 'MADE EASY AND QUICK' },
                { key: 'sub_label', label: 'Alt Etiket', placeholder: 'For Exhibitors' },
                { key: 'description', label: 'Açıklama', placeholder: "We've developed..." },
                { key: 'world_label', label: 'Dünya Etiketi', placeholder: 'ALL OVER THE WORLD' },
              ].map(f => (
                <div key={f.key}>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">{f.label}</label>
                  {f.key === 'heading' || f.key === 'description' ? (
                    <textarea value={content.made_easy?.[f.key] || ''} onChange={e => updateText('made_easy', f.key, e.target.value)}
                      rows={f.key === 'description' ? 3 : 2} placeholder={f.placeholder}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-400" />
                  ) : (
                    <Input value={content.made_easy?.[f.key] || ''} placeholder={f.placeholder}
                      onChange={e => updateText('made_easy', f.key, e.target.value)} className="h-9 text-sm" />
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3 border-b border-gray-50">
              <CardTitle className="text-sm">Dizin / Hero Bölümü</CardTitle>
            </CardHeader>
            <CardContent className="p-5 space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">Dizin Başlığı</label>
                <textarea value={content.directory?.headline || ''}
                  onChange={e => updateText('directory', 'headline', e.target.value)}
                  rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-400" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">Dizin Alt Metni</label>
                <Input value={content.directory?.subtext || ''}
                  onChange={e => updateText('directory', 'subtext', e.target.value)} className="h-9 text-sm" />
              </div>
              <hr className="border-gray-100" />
              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">Hero Açıklaması</label>
                <textarea value={content.hero?.description || ''}
                  onChange={e => updateText('hero', 'description', e.target.value)}
                  rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {/* ── SUPPLIER CARDS TAB ────────────────────────────────────────────── */}
      {activeTab === 'supplier_cards' && content && (
        <div className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3 border-b border-gray-50 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-sm">Tedarikçi Kategori Kartları</CardTitle>
                <p className="text-xs text-gray-500 mt-1">Ana sayfadaki 2×4 kart ızgarasını yönet. Her kart "/find-suppliers?product=..." sayfasına yönlendirir.</p>
              </div>
              <button
                onClick={() => {
                  const cards = content.supplier_cards || [];
                  const newCard = {
                    id: Date.now().toString(),
                    title: 'Yeni Kategori',
                    bg_color: '#0B1E4D',
                    text_color: '#ffffff',
                    link: '/find-suppliers',
                    variant: 'standard',
                  };
                  setContent(prev => ({ ...prev, supplier_cards: [...cards, newCard] }));
                  setDirty(true);
                }}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors">
                <Plus className="w-3.5 h-3.5" /> Kart Ekle
              </button>
            </CardHeader>
            <CardContent className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(content.supplier_cards || []).map((card, i) => (
                  <div key={card.id || i}
                    className="border border-gray-200 rounded-xl p-4 space-y-3 hover:border-orange-300 transition-colors">
                    {/* Preview */}
                    <div className="rounded-xl h-20 relative flex items-end p-3 overflow-hidden"
                      style={{ backgroundColor: card.bg_color || '#0B1E4D' }}>
                      {card.variant === 'image' && card.image && (
                        <img src={card.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
                      )}
                      <span className="relative font-bold text-sm leading-tight z-10"
                        style={{ color: card.text_color || '#ffffff' }}>{card.title}</span>
                      <span className="absolute top-2 right-2 text-[10px] px-1.5 py-0.5 bg-black/20 text-white rounded-full z-10">
                        {card.variant}
                      </span>
                    </div>

                    {/* Fields */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] font-semibold text-gray-500 block mb-1">Başlık</label>
                        <Input value={card.title} onChange={e => {
                          const cards = [...(content.supplier_cards || [])];
                          cards[i] = { ...cards[i], title: e.target.value };
                          setContent(p => ({ ...p, supplier_cards: cards })); setDirty(true);
                        }} className="h-8 text-xs" />
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold text-gray-500 block mb-1">Tip</label>
                        <select value={card.variant}
                          onChange={e => {
                            const cards = [...(content.supplier_cards || [])];
                            cards[i] = { ...cards[i], variant: e.target.value };
                            setContent(p => ({ ...p, supplier_cards: cards })); setDirty(true);
                          }}
                          className="w-full h-8 border border-gray-200 rounded-lg px-2 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400">
                          <option value="standard">Standard</option>
                          <option value="decorative">Dekoratif</option>
                          <option value="cta">CTA (büyük)</option>
                          <option value="image">Fotoğraflı</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] font-semibold text-gray-500 block mb-1">Arka Plan Rengi</label>
                        <div className="flex gap-1.5">
                          <input type="color" value={card.bg_color || '#0B1E4D'}
                            onChange={e => {
                              const cards = [...(content.supplier_cards || [])];
                              cards[i] = { ...cards[i], bg_color: e.target.value };
                              setContent(p => ({ ...p, supplier_cards: cards })); setDirty(true);
                            }} className="w-8 h-8 rounded cursor-pointer border-0" />
                          <Input value={card.bg_color || ''} onChange={e => {
                            const cards = [...(content.supplier_cards || [])];
                            cards[i] = { ...cards[i], bg_color: e.target.value };
                            setContent(p => ({ ...p, supplier_cards: cards })); setDirty(true);
                          }} className="h-8 text-xs flex-1" placeholder="#0B1E4D" />
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold text-gray-500 block mb-1">Metin Rengi</label>
                        <div className="flex gap-1.5">
                          <input type="color" value={card.text_color || '#ffffff'}
                            onChange={e => {
                              const cards = [...(content.supplier_cards || [])];
                              cards[i] = { ...cards[i], text_color: e.target.value };
                              setContent(p => ({ ...p, supplier_cards: cards })); setDirty(true);
                            }} className="w-8 h-8 rounded cursor-pointer border-0" />
                          <Input value={card.text_color || ''} onChange={e => {
                            const cards = [...(content.supplier_cards || [])];
                            cards[i] = { ...cards[i], text_color: e.target.value };
                            setContent(p => ({ ...p, supplier_cards: cards })); setDirty(true);
                          }} className="h-8 text-xs flex-1" placeholder="#ffffff" />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] font-semibold text-gray-500 block mb-1 flex items-center gap-1">
                        <Link className="w-3 h-3" /> Hedef URL
                      </label>
                      <Input value={card.link || ''} onChange={e => {
                        const cards = [...(content.supplier_cards || [])];
                        cards[i] = { ...cards[i], link: e.target.value };
                        setContent(p => ({ ...p, supplier_cards: cards })); setDirty(true);
                      }} className="h-8 text-xs" placeholder="/find-suppliers?product=Catering" />
                    </div>

                    {card.variant === 'image' && (
                      <div>
                        <label className="text-[10px] font-semibold text-gray-500 block mb-1">Fotoğraf URL</label>
                        <Input value={card.image || ''} onChange={e => {
                          const cards = [...(content.supplier_cards || [])];
                          cards[i] = { ...cards[i], image: e.target.value };
                          setContent(p => ({ ...p, supplier_cards: cards })); setDirty(true);
                        }} className="h-8 text-xs" placeholder="https://..." />
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-1">
                      <div className="flex gap-1">
                        <button onClick={() => {
                          if (i === 0) return;
                          const cards = [...(content.supplier_cards || [])];
                          [cards[i-1], cards[i]] = [cards[i], cards[i-1]];
                          setContent(p => ({ ...p, supplier_cards: cards })); setDirty(true);
                        }} disabled={i === 0} className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 text-xs border rounded">↑</button>
                        <button onClick={() => {
                          const cards = content.supplier_cards || [];
                          if (i >= cards.length - 1) return;
                          const nc = [...cards];
                          [nc[i], nc[i+1]] = [nc[i+1], nc[i]];
                          setContent(p => ({ ...p, supplier_cards: nc })); setDirty(true);
                        }} disabled={i >= (content.supplier_cards || []).length - 1}
                          className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30 text-xs border rounded">↓</button>
                      </div>
                      <button onClick={() => {
                        if ((content.supplier_cards || []).length <= 2) return;
                        const cards = (content.supplier_cards || []).filter((_, idx) => idx !== i);
                        setContent(p => ({ ...p, supplier_cards: cards })); setDirty(true);
                      }}
                        className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── PREMIUM SUPPLIERS TAB ─────────────────────────────────────────── */}
      {activeTab === 'premium_suppliers' && content && (() => {
        const ps = content.premium_suppliers_section || {};
        const update = (field, val) => {
          setContent(p => ({
            ...p,
            premium_suppliers_section: { ...(p.premium_suppliers_section || {}), [field]: val }
          }));
          setDirty(true);
        };
        const updateFeature = (i, val) => {
          const arr = [...(ps.features || [])];
          arr[i] = val;
          update('features', arr);
        };
        const addFeature = () => update('features', [...(ps.features || []), 'Yeni Özellik']);
        const removeFeature = (i) => update('features', (ps.features || []).filter((_, idx) => idx !== i));
        const updateProcess = (i, field, val) => {
          const arr = [...(ps.process_items || [])];
          arr[i] = { ...arr[i], [field]: val };
          update('process_items', arr);
        };
        const addProcess = () => update('process_items', [...(ps.process_items || []), { label: 'Yeni Adım', value: 'Açıklama', color: 'blue' }]);
        const removeProcess = (i) => update('process_items', (ps.process_items || []).filter((_, idx) => idx !== i));
        return (
          <div className="space-y-6">
            {/* Badge & Titles */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3 border-b border-gray-50">
                <CardTitle className="text-sm">Başlık & Rozet</CardTitle>
              </CardHeader>
              <CardContent className="p-5 space-y-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Rozet Metni</label>
                  <Input value={ps.badge_text || ''} onChange={e => update('badge_text', e.target.value)} className="h-9 text-sm" placeholder="Verified Excellence" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">Başlık Satır 1 (büyük)</label>
                    <Input value={ps.title_line1 || ''} onChange={e => update('title_line1', e.target.value)} className="h-9 text-sm" placeholder="PREMIUM" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">Başlık Satır 2 (renkli)</label>
                    <Input value={ps.title_line2 || ''} onChange={e => update('title_line2', e.target.value)} className="h-9 text-sm" placeholder="SUPPLIERS" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Açıklama 1</label>
                  <textarea value={ps.description1 || ''} onChange={e => update('description1', e.target.value)} rows={3}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-400" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Açıklama 2</label>
                  <textarea value={ps.description2 || ''} onChange={e => update('description2', e.target.value)} rows={2}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-400" />
                </div>
              </CardContent>
            </Card>

            {/* Features */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3 border-b border-gray-50 flex flex-row items-center justify-between">
                <CardTitle className="text-sm">Özellikler Listesi</CardTitle>
                <button onClick={addFeature} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors">
                  <Plus className="w-3.5 h-3.5" /> Ekle
                </button>
              </CardHeader>
              <CardContent className="p-5 space-y-2">
                {(ps.features || []).map((feat, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input value={feat} onChange={e => updateFeature(i, e.target.value)} className="h-8 text-sm flex-1" />
                    <button onClick={() => removeFeature(i)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* CTA Buttons */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3 border-b border-gray-50">
                <CardTitle className="text-sm">CTA Butonları</CardTitle>
              </CardHeader>
              <CardContent className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">Birincil Buton Metni</label>
                    <Input value={ps.cta_primary_text || ''} onChange={e => update('cta_primary_text', e.target.value)} className="h-9 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">Birincil Buton URL</label>
                    <Input value={ps.cta_primary_link || ''} onChange={e => update('cta_primary_link', e.target.value)} className="h-9 text-sm" placeholder="/find-suppliers" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">İkincil Buton Metni</label>
                    <Input value={ps.cta_secondary_text || ''} onChange={e => update('cta_secondary_text', e.target.value)} className="h-9 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">İkincil Buton URL</label>
                    <Input value={ps.cta_secondary_link || ''} onChange={e => update('cta_secondary_link', e.target.value)} className="h-9 text-sm" placeholder="/find-suppliers" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Image & Stats */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3 border-b border-gray-50">
                <CardTitle className="text-sm">Görsel & İstatistikler</CardTitle>
              </CardHeader>
              <CardContent className="p-5 space-y-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Görsel URL</label>
                  <Input value={ps.image || ''} onChange={e => update('image', e.target.value)} className="h-9 text-sm" placeholder="https://..." />
                  {ps.image && <img src={ps.image} alt="preview" className="mt-2 w-full h-28 object-cover rounded-lg" />}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">Görsel Başlığı</label>
                    <Input value={ps.image_title || ''} onChange={e => update('image_title', e.target.value)} className="h-9 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">Görsel Alt Başlığı</label>
                    <Input value={ps.image_subtitle || ''} onChange={e => update('image_subtitle', e.target.value)} className="h-9 text-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">İstatistik 1 Değer</label>
                    <Input value={ps.stat1_value || ''} onChange={e => update('stat1_value', e.target.value)} className="h-9 text-sm" placeholder="15+" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">İstatistik 1 Etiket</label>
                    <Input value={ps.stat1_label || ''} onChange={e => update('stat1_label', e.target.value)} className="h-9 text-sm" placeholder="Years Experience" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">İstatistik 2 Değer</label>
                    <Input value={ps.stat2_value || ''} onChange={e => update('stat2_value', e.target.value)} className="h-9 text-sm" placeholder="500+" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">İstatistik 2 Etiket</label>
                    <Input value={ps.stat2_label || ''} onChange={e => update('stat2_label', e.target.value)} className="h-9 text-sm" placeholder="Stands Built" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Process Items */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3 border-b border-gray-50 flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-sm">Süreç Adımları</CardTitle>
                  <p className="text-xs text-gray-500 mt-1">Sağ paneldeki "Craftsmanship Process" adımları</p>
                </div>
                <button onClick={addProcess} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors">
                  <Plus className="w-3.5 h-3.5" /> Adım Ekle
                </button>
              </CardHeader>
              <CardContent className="p-5 space-y-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Süreç Bölümü Başlığı</label>
                  <Input value={ps.process_title || ''} onChange={e => update('process_title', e.target.value)} className="h-9 text-sm" placeholder="Craftsmanship Process" />
                </div>
                <div className="space-y-2 mt-3">
                  {(ps.process_items || []).map((item, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center border border-gray-100 rounded-lg p-3">
                      <div className="col-span-4">
                        <label className="text-[10px] text-gray-400">Etiket</label>
                        <Input value={item.label} onChange={e => updateProcess(i, 'label', e.target.value)} className="h-7 text-xs" />
                      </div>
                      <div className="col-span-4">
                        <label className="text-[10px] text-gray-400">Değer</label>
                        <Input value={item.value} onChange={e => updateProcess(i, 'value', e.target.value)} className="h-7 text-xs" />
                      </div>
                      <div className="col-span-3">
                        <label className="text-[10px] text-gray-400">Renk</label>
                        <select value={item.color} onChange={e => updateProcess(i, 'color', e.target.value)}
                          className="w-full h-7 border border-gray-200 rounded px-1 text-xs focus:outline-none">
                          {['green','blue','purple','orange','red','yellow','teal'].map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <button onClick={() => removeProcess(i)} className="p-1 text-red-400 hover:text-red-600">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        );
      })()}

      {/* ── TESTIMONIALS TAB ──────────────────────────────────────────────────── */}
      {activeTab === 'testimonials' && content && (() => {
        const ts = content.testimonials_section || {};
        const items = ts.items || [];
        const stats = ts.stats || [];
        const updateField = (f, v) => { setContent(p => ({ ...p, testimonials_section: { ...(p.testimonials_section || {}), [f]: v } })); setDirty(true); };
        const updateItem = (i, f, v) => {
          const arr = [...items]; arr[i] = { ...arr[i], [f]: v };
          updateField('items', arr);
        };
        const addItem = () => updateField('items', [...items, { id: Date.now().toString(), name: 'Yeni Üye', role: 'Rol / Şirket', tag: 'Exhibitor', tag_color: 'blue', text: 'Yorum metni...', avatar: '', verified_label: 'Verified Member' }]);
        const removeItem = (i) => updateField('items', items.filter((_, idx) => idx !== i));
        const updateStat = (i, f, v) => { const arr = [...stats]; arr[i] = { ...arr[i], [f]: v }; updateField('stats', arr); };
        const addStat = () => updateField('stats', [...stats, { value: '0', label: 'Yeni Stat' }]);
        const removeStat = (i) => updateField('stats', stats.filter((_, idx) => idx !== i));
        return (
          <div className="space-y-6">
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3 border-b border-gray-50"><CardTitle className="text-sm">Bölüm Başlığı</CardTitle></CardHeader>
              <CardContent className="p-5 space-y-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Ana Başlık</label>
                  <Input value={ts.heading || ''} onChange={e => updateField('heading', e.target.value)} className="h-9 text-sm" placeholder="What Our Members Say" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Alt Açıklama</label>
                  <textarea value={ts.subheading || ''} onChange={e => updateField('subheading', e.target.value)} rows={2}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-orange-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3 border-b border-gray-50 flex flex-row items-center justify-between">
                <CardTitle className="text-sm">Üye Yorumları ({items.length})</CardTitle>
                <button onClick={addItem} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors">
                  <Plus className="w-3.5 h-3.5" /> Yorum Ekle
                </button>
              </CardHeader>
              <CardContent className="p-5 space-y-4">
                {items.map((item, i) => (
                  <div key={item.id || i} className="border border-gray-100 rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-gray-600">Yorum #{i + 1}</span>
                      <button onClick={() => removeItem(i)} className="p-1 text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] text-gray-400 font-semibold block mb-1">Ad / Şirket</label>
                        <Input value={item.name || ''} onChange={e => updateItem(i, 'name', e.target.value)} className="h-8 text-xs" />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-400 font-semibold block mb-1">Rol</label>
                        <Input value={item.role || ''} onChange={e => updateItem(i, 'role', e.target.value)} className="h-8 text-xs" />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-[10px] text-gray-400 font-semibold block mb-1">Rozet Tipi</label>
                        <Input value={item.tag || ''} onChange={e => updateItem(i, 'tag', e.target.value)} className="h-8 text-xs" placeholder="Exhibitor" />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-400 font-semibold block mb-1">Rozet Rengi</label>
                        <select value={item.tag_color || 'blue'} onChange={e => updateItem(i, 'tag_color', e.target.value)}
                          className="w-full h-8 border border-gray-200 rounded px-2 text-xs focus:outline-none">
                          {['blue','orange','teal','green','purple','gray'].map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-400 font-semibold block mb-1">Doğrulama Etiketi</label>
                        <Input value={item.verified_label || ''} onChange={e => updateItem(i, 'verified_label', e.target.value)} className="h-8 text-xs" />
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-400 font-semibold block mb-1">Yorum Metni</label>
                      <textarea value={item.text || ''} onChange={e => updateItem(i, 'text', e.target.value)} rows={3}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-xs resize-none focus:outline-none focus:ring-2 focus:ring-orange-400" />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-400 font-semibold block mb-1">Avatar URL</label>
                      <div className="flex gap-2 items-center">
                        <Input value={item.avatar || ''} onChange={e => updateItem(i, 'avatar', e.target.value)} className="h-8 text-xs flex-1" placeholder="https://..." />
                        {item.avatar && <img src={item.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />}
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3 border-b border-gray-50 flex flex-row items-center justify-between">
                <CardTitle className="text-sm">Alt İstatistikler ({stats.length})</CardTitle>
                <button onClick={addStat} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors">
                  <Plus className="w-3.5 h-3.5" /> Stat Ekle
                </button>
              </CardHeader>
              <CardContent className="p-5 space-y-2">
                {stats.map((s, i) => (
                  <div key={i} className="flex gap-3 items-center">
                    <Input value={s.value || ''} onChange={e => updateStat(i, 'value', e.target.value)} className="h-8 text-sm w-24 font-bold" placeholder="2,500+" />
                    <Input value={s.label || ''} onChange={e => updateStat(i, 'label', e.target.value)} className="h-8 text-sm flex-1" placeholder="Verified Matches" />
                    <button onClick={() => removeStat(i)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        );
      })()}

    </div>
  );
};

export default UltraCMS;
